import { Component, ChangeDetectorRef, OnInit, OnDestroy, inject, ElementRef, effect, viewChild, DestroyRef, signal } from "@angular/core";
import { Router } from "@angular/router";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { Subscription, Observable, map, firstValueFrom } from "rxjs";
import { NgxPaginationModule } from "ngx-pagination";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";
import { UntypedFormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { TooltipDirective, PopoverContainerDirective, PopoverComponent } from "@progress/kendo-angular-tooltip";
import { NewUserRequestDataModel, UserDataModel, IUserUpdateData } from "../../settings-interfaces";
import { DashboardAccessComponent } from "./dashboard-access/dashboard-access.component";
import { CancelConfirmationComponent } from "./cancel-confirmation/cancel-confirmation.component";
import { DialogService } from "@progress/kendo-angular-dialog";

import {
    DashboardsService,
    HelpersService,
    ResponseErrorHandlerService,
    IFirmUsersService,
    IFirmGroupsService,
    IFirmUserRolesService,
    UsersService,
    IFirmRolesService,
    UserDashboardSettingsService,
    AuthService,
    SettingsBreadcrumbService,
    WkTranslatePipe,
} from "../../shared";
import { UtilsService } from "../../shared/utils/utils.service";
import type { DashboardSelectCanChangeArgs } from "@shared/dashboard-select";

class OptionModelFilter {
    options: any[];
    allOptions: any[];
    selectedValues: any[];
}

type ManagementUser = UserDataModel & { toUpdate?: boolean };
@Component({
    selector: "wk-settings-dashboard-management",
    templateUrl: "./settings-dashboard-management.component.html",
    styleUrl: "./settings-dashboard-management.component.scss",
    standalone: false,
    host: { class: "wk-sdm" },
    providers: [NgxPaginationModule, WkTranslatePipe],
})
export class SettingsDashboardManagementComponent implements OnInit, OnDestroy {
    private readonly dashboardsService = inject(DashboardsService);
    private readonly usersService = inject(UsersService);
    private readonly helpersService = inject(HelpersService);
    private readonly authService = inject(AuthService);
    private readonly iFirmUsersService = inject(IFirmUsersService);
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    private readonly userDashboardSettingsService = inject(UserDashboardSettingsService);
    private readonly iFirmUserRolesService = inject(IFirmUserRolesService);
    private readonly translateService = inject(TranslateService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly modalService = inject(BsModalService);
    private readonly dialogService = inject(DialogService);
    private readonly breadcrumbService = inject(SettingsBreadcrumbService);
    private readonly router = inject(Router);

    bsModalRef?: BsModalRef;
    subscription: Subscription;
    model = [];
    allModel = [];
    loading = false;
    page = 1;
    pageSize = signal<number>(15);
    selectAll: boolean | null = null;
    selectedCount = 0;
    group1Enabled = false;
    group2Enabled = false;
    group1Options: OptionModelFilter;
    group2Options: OptionModelFilter;
    rolesOptions: OptionModelFilter;
    accessOptions: OptionModelFilter;
    currentLanguage = "en-CA";
    inputTerm = "";
    term = new UntypedFormControl();
    isSaving = false;
    currentDashboard: IDashboardModel;
    newDashboardId: string;
    sortDirAsc: boolean | undefined = undefined;
    sortMember = "LastName";
    readonly group1PopoverTrigger = viewChild<PopoverContainerDirective>("group1PopoverTrigger");
    readonly popoverGroup1 = viewChild<PopoverComponent>("Group1Popover");
    readonly group2PopoverTrigger = viewChild<PopoverContainerDirective>("group2PopoverTrigger");
    readonly popoverGroup2 = viewChild<PopoverComponent>("Group2Popover");
    readonly rolesPopoverTrigger = viewChild<PopoverContainerDirective>("rolesPopoverTrigger");
    readonly popoverRoles = viewChild.required<PopoverComponent>("RolesPopover");
    readonly accessPopoverTrigger = viewChild<PopoverContainerDirective>("accessPopoverTrigger");
    readonly popoverAccess = viewChild.required<PopoverComponent>("AccessPopover");
    readonly tooltipDir = viewChild<TooltipDirective>(TooltipDirective);
    private tableContainer = viewChild<ElementRef<HTMLElement>>("tableContainer");
    private tableHeader = viewChild<ElementRef<HTMLElement>>("tableHeader");
    private destroyRef = inject(DestroyRef);
    private readonly wkTranslatePipe = inject(WkTranslatePipe);

    isFormChanged = false;
    public settingPageNumber = 1;
    public maxSettingPageSize = 1;
    public settingPageSize = 10;
    public settingPagedItems: any[];
    public totalItemsCount = 0;
    public searchText = "";

    public settingPageNumber_Group2 = 1;
    public maxSettingPageSize_Group2 = 1;
    public settingPageSize_Group2 = 10;
    public settingPagedItems_Group2: any[];
    public totalItemsCount_Group2 = 0;
    public searchText_Group2 = "";

    public settingPageNumber_Roles = 1;
    public maxSettingPageSize_Roles = 1;
    public settingPageSize_Roles = 10;
    public settingPagedItems_Roles: any[];
    public totalItemsCount_Roles = 0;
    public searchText_Roles = "";

    public settingPageNumber_Access = 1;
    public maxSettingPageSize_Access = 1;
    public settingPageSize_Access = 10;
    public settingPagedItems_Access: any[];
    public totalItemsCount_Access = 0;
    public searchText__Access = "";
    checkDashboardVisibility: (dashboard: IDashboardModel) => boolean = (dashboard) => {
        return (this.dashboardsService.getDashboardYear(dashboard) ?? 9999) > 2023;
    };

    constructor() {
        this.currentLanguage = this.translateService.getCurrentLang()
            ? this.translateService.getCurrentLang()
            : this.translateService.getFallbackLang();
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang;
        });

        this.subscription = this.dashboardsService.currentDashboardChanged$.subscribe(() => {
            this.init();
        });

        effect(() => {
            const elRef = this.tableContainer();
            const headerRef = this.tableHeader();
            if (!elRef || !headerRef) return;

            const el = elRef.nativeElement;
            const headerEl = headerRef.nativeElement;
            const height = el.getBoundingClientRect().height - headerEl.getBoundingClientRect().height;
            const maxRows = Math.floor(height / 42.8);
            this.pageSize.set(maxRows);

            const ro = new ResizeObserver((entries) => {
                for (const e of entries) {
                    const height = el.getBoundingClientRect().height - headerEl.getBoundingClientRect().height;
                    const maxRows = Math.floor(height / 42.8);
                    this.pageSize.set(maxRows);
                }
            });
            ro.observe(el);

            this.destroyRef.onDestroy(() => ro.disconnect());
            return () => ro.disconnect();
        });

        effect(() => {
            const trigger = this.group1PopoverTrigger();
            const popover = this.popoverGroup1();
            if (!trigger || !popover) return;
            trigger.popover = (_anchor: Element) => popover;
        });

        effect(() => {
            const trigger = this.group2PopoverTrigger();
            const popover = this.popoverGroup2();
            if (!trigger || !popover) return;
            trigger.popover = (_anchor: Element) => popover;
        });

        effect(() => {
            const trigger = this.rolesPopoverTrigger();
            const popover = this.popoverRoles();
            if (!trigger || !popover) return;
            trigger.popover = (_anchor: Element) => popover;
        });

        effect(() => {
            const trigger = this.accessPopoverTrigger();
            const popover = this.popoverAccess();
            if (!trigger || !popover) return;
            trigger.popover = (_anchor: Element) => popover;
        });
    }

    ngOnInit(): void {
        this.breadcrumbService.setBreadcrumbs("dashboardmanagement");
        (async () => {
            this.currentDashboard = await this.dashboardsService.getDashboard(this.newDashboardId);
            if (this.authService.loggedIn()) {
                await this.init();
                this.term.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
                    this.filterModel();
                    this.resetToFirstPageIfNecessary();
                });
            }
        })();
    }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    async init() {
        await this.iFirmGroupsService.getiFirmGroups();
        await this.iFirmRolesService.getiFirmRoles();
        await this.iFirmUserRolesService.getiFirmUserRoles();
        await this.usersService.refresh();

        this.group1Enabled = this.iFirmGroupsService.getGroup1Enabled();
        this.group2Enabled = this.iFirmGroupsService.getGroup2Enabled();
        this.group1Options = {
            options: [],
            allOptions: this.iFirmGroupsService.getGroup1Options().map((option) => ({ id: option.Id.toString(), value: option })),
            selectedValues: [],
        } as OptionModelFilter;
        this.group2Options = {
            options: [],
            allOptions: this.iFirmGroupsService.getGroup2Options().map((option) => ({ id: option.Id.toString(), value: option })),
            selectedValues: [],
        } as OptionModelFilter;

        await this.syncLocalModel();
        this.settingPagedItems = this.group1Options.options;
        this.settingPagedItems_Group2 = this.group2Options.options;
        this.settingPagedItems_Roles = this.rolesOptions.options;
        this.settingPagedItems_Access = this.accessOptions.options;
        this.helpersService.suppressSpinner();
    }

    async syncLocalModel() {
        this.model = UtilsService.recursiveDeepCopy(await this.iFirmUsersService.getUsers()).filter((it) => it.Active && it.LicenseEnabled);

        if (this.newDashboardId) {
            this.currentDashboard = await this.dashboardsService.getDashboard(this.newDashboardId);
            this.newDashboardId = undefined;
        } else {
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        }

        const uds = await this.userDashboardSettingsService.getUserDashboardSettingsForDashboard(this.currentDashboard.Id);

        const userRoles = await this.iFirmUserRolesService.getiFirmUserRoles();
        const roles: any = this.iFirmRolesService.iFirmRoles;
        this.rolesOptions = {
            options: [],
            allOptions: [
                { id: "none", value: { Description: { "en-CA": "None", "fr-CA": "Aucun" } } },
                ...roles.map((option) => ({ id: option.Id.toString(), value: option })),
            ],
            selectedValues: [],
        } as OptionModelFilter;
        this.accessOptions = {
            options: [],
            allOptions: [
                { id: "true", value: { "en-CA": "Yes", "fr-CA": "Oui" } },
                { id: "false", value: { "en-CA": "No", "fr-CA": "Non" } },
            ],
            selectedValues: [],
        } as OptionModelFilter;
        this.accessOptions.options = this.accessOptions.allOptions;
        this.model.forEach((item) => {
            let user = this.usersService.getUserById(item.UserId?.toString()) as ManagementUser;
            if (user && user.Id === user.IFirmUserId) {
                // Not tracking user, try to match by name or email
                const matchedUser = this.usersService.Users.find(
                    (it) =>
                        (it.FirstName.toLowerCase() === user?.FirstName.toLowerCase() &&
                            it.LastName.toLowerCase() === user?.LastName.toLowerCase()) ||
                        it.Email?.toLowerCase() === user?.Email?.toLowerCase(),
                );
                if (matchedUser) {
                    const id = user.Id;
                    user = UtilsService.recursiveDeepCopy(matchedUser) as ManagementUser;
                    user.IFirmUserId = id;
                    user.toUpdate = true;
                }
            }
            const userUds = user && uds.find((it) => it.UserId == user.Id);
            item.HasAccess = !userUds || userUds.Active;
            item.OrgHasAccess = !userUds || userUds.Active;
            item.Selected = false;
            item.User = user;
            item.UserUds = userUds;
            item.Roles = userRoles.filter((it) => it.UserId == item.UserId?.toString());
            item.RolesJoined = item.Roles.map((it) => this.wkTranslatePipe.transform(roles.find((r) => r.Id == it.RoleId)?.Description))?.join(", ");
        });
        this.allModel = this.model;
        this.onSortChange(undefined);
    }

    onCancelChanges() {
        this.canChangeDashboard(null);
    }

    doChangeDashboard(args) {
        this.newDashboardId = args.dashboardId;
        this.resetToFirstPageIfNecessary();
        args.action(args.dashboardId);
        this.dashboardsService.setCurrentDashboardId(args.dashboardId);
        this.sortDirAsc = undefined;
        this.sortMember = "LastName";
        this.inputTerm = "";
        this.group1Options.selectedValues = [];
        this.group2Options.selectedValues = [];
        this.syncLocalModel();
    }

    canChangeDashboard(args: DashboardSelectCanChangeArgs) {
        if (this.allModel.filter((it) => it.HasAccess != it.OrgHasAccess).length > 0 || this.isFormChanged) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result <= 2) {
                    if (result === 1) {
                        if (args) {
                            this.isFormChanged = false;
                            this.doChangeDashboard(args);
                        } else {
                            this.router.navigate(["/settings/main"]);
                        }
                    } else {
                        if (args) {
                            args.cancelAction();
                        }
                    }
                } else if (args) {
                    args.cancelAction();
                }
            });
        } else {
            if (args) {
                this.doChangeDashboard(args);
            } else {
                this.router.navigate(["/settings/main"]);
            }
        }
    }

    openPendingChangesDialogConfirmation(): Observable<number> {
        const dialog = this.dialogService.open({
            title: this.translateService.instant("AccessPendingChanges.Title"),
            content: CancelConfirmationComponent,
            actions: [
                { text: this.translateService.instant("General.Discard"), primary: true, choice: 1 }, // Discard and proceed
                { text: this.translateService.instant("General.Cancel"), choice: 2 }, // Cancel
            ],
            width: 600,
            cssClass: "access-pending-changes-dialog",
            autoFocusedElement: ".k-dialog-titlebar",
        });
        return dialog.result.pipe(
            map((result: any) => {
                return result.choice;
            }),
        );
    }

    onBreadcrumbNavigate(event: { route: string; proceed: () => void }): void {
        if (this.allModel.filter((it) => it.HasAccess != it.OrgHasAccess).length > 0 || this.isFormChanged) {
            this.openPendingChangesDialogConfirmation().subscribe((result) => {
                if (result === 1) {
                    this.isFormChanged = false;
                    event.proceed();
                }
            });
        } else {
            event.proceed();
        }
    }

    async createUser(it) {
        let result = true;
        const newUser = new NewUserRequestDataModel();
        newUser.Email = it.Email;
        newUser.IFirmUserId = it.UserId.toString();
        newUser.FirstName = it.FirstName;
        newUser.LastName = it.LastName;
        newUser.CommunicationLanguage = it.UserCulture && it.UserCulture != "" ? it.UserCulture : "en-CA";
        newUser.LastDashboardId = this.currentDashboard.Id;
        newUser.IsAdmin = false;
        newUser.IsSupport = false;

        try {
            const userData = await firstValueFrom(
                this.usersService.addUser(newUser).pipe(
                    map((data) => {
                        return data;
                    }),
                ),
            );
            if (userData?.User?.Id) {
                it.User = userData.User;
            }
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "CancelSaved",
            });
            result = false;
        }
        return result;
    }

    async updateUser(user: ManagementUser) {
        let result = true;
        const newUser: IUserUpdateData = {
            Email: user.Email,
            IFirmUserId: user.IFirmUserId,
            FirstName: user.FirstName,
            LastName: user.LastName,
            CommunicationLanguage: user.UserCulture && user.UserCulture != "" ? user.UserCulture : "en-CA",
        };

        try {
            await firstValueFrom(
                this.usersService.updateUser(user.Id, newUser).pipe(
                    map((data) => {
                        return data;
                    }),
                ),
            );
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "CancelSaved",
            });
            result = false;
        }
        return result;
    }

    async createUserDashboard(active: boolean, it) {
        let result = true;
        const createUserDashboard = {
            Active: active,
            UserId: it.User.Id,
            DashboardId: this.currentDashboard.Id,
            Settings: {
                AutomaticSyncOpen: false,
                AutomaticSyncClose: false,
                IsManager: false,
            },
        };
        try {
            const uds = await firstValueFrom(
                this.userDashboardSettingsService.addUserDashboardSettings(createUserDashboard).pipe(
                    map((data) => {
                        return data;
                    }),
                ),
            );
            this.usersService.pushUDSIdInMergedUserData(
                uds.UserDashboardSetting.UserId,
                uds.UserDashboardSetting.DashboardId,
                uds.UserDashboardSetting.Id,
            );
            it.OrgHasAccess = it.HasAccess;
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "CancelSaved",
            });
            result = false;
        }
        return result;
    }

    async onSave() {
        if (!this.isSaving) {
            this.isSaving = true;
            let refreshUds = false;
            let refreshUsers = false;
            let hasError = false;
            for (const it of this.allModel) {
                if (!hasError) {
                    if (it.HasAccess != it.OrgHasAccess || (it.HasAccess && it.User?.toUpdate)) {
                        if (it.HasAccess) {
                            if (!it.UserUds?.Active) {
                                if (!it.User || it.User.Id === it.User.IFirmUserId) {
                                    if (await this.createUser(it)) {
                                        refreshUsers = true;
                                    } else {
                                        hasError = true;
                                    }
                                } else if (it.User.toUpdate) {
                                    if (await this.updateUser(it.User)) {
                                        refreshUsers = true;
                                    } else {
                                        hasError = true;
                                    }
                                }
                                if (!hasError) {
                                    if (await this.createUserDashboard(true, it)) {
                                        refreshUds = true;
                                    } else {
                                        hasError = true;
                                    }
                                }
                            }
                        } else if (it.UserUds?.Active) {
                            try {
                                await firstValueFrom(
                                    this.userDashboardSettingsService.deleteUserDashboardSettings(it.UserUds.Id).pipe(
                                        map((data) => {
                                            return data;
                                        }),
                                    ),
                                );
                                this.usersService.deleteUDSFromUser(it.UserUds.Id, it.UserUds.UserId);
                                refreshUds = true;
                                it.OrgHasAccess = it.HasAccess;
                            } catch {
                                this.responseErrorHandlerService.showPopup({
                                    typeLayout: "ERROR",
                                    messageCode: "CancelSaved",
                                });
                                hasError = true;
                            }
                        } else if (!it.UserUds || it.User?.toUpdate) {
                            if (!it.User || it.User.Id === it.User.IFirmUserId) {
                                if (await this.createUser(it)) {
                                    refreshUsers = true;
                                } else {
                                    hasError = true;
                                }
                            } else if (it.User.toUpdate) {
                                if (await this.updateUser(it.User)) {
                                    refreshUsers = true;
                                } else {
                                    hasError = true;
                                }
                            }
                            if (!hasError) {
                                if (await this.createUserDashboard(false, it)) {
                                    refreshUds = true;
                                } else {
                                    hasError = true;
                                }
                            }
                        }
                    }
                }
            }
            if (refreshUsers) {
                this.usersService.refresh();
                await this.usersService.getUsersList();
            }
            if (refreshUds) {
                this.userDashboardSettingsService.refreshForDashboard(this.currentDashboard.Id, true);
            }
            if (!hasError) {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "SUCCESS",
                    messageCode: "successfullySaved",
                });
            }
            this.isSaving = false;
            this.isFormChanged = false;
            if (refreshUsers || refreshUds) {
                await this.syncLocalModel();
            }
        }
    }

    getGroup1UsersEnabled() {
        return this.iFirmGroupsService.getGroup1UsersEnabled();
    }

    getGroup2UsersEnabled() {
        return this.iFirmGroupsService.getGroup2UsersEnabled();
    }

    getGroup1Caption() {
        return this.iFirmGroupsService.getGroup1Caption(this.currentLanguage);
    }

    getGroup2Caption() {
        return this.iFirmGroupsService.getGroup2Caption(this.currentLanguage);
    }

    getGroupOptionCaption(groupObj, id): string {
        if (groupObj) {
            return groupObj.find((group) => group.id == id)?.value[this.currentLanguage];
        }
        return null;
    }

    getGroup1OptionCaption(id): string {
        return this.getGroupOptionCaption(this.group1Options.allOptions, id);
    }

    getGroup2OptionCaption(id): string {
        return this.getGroupOptionCaption(this.group2Options.allOptions, id);
    }

    getRoleOptionCaption(id): string {
        return this.wkTranslatePipe.transform(this.rolesOptions.allOptions.find((role) => role.id == id).value?.Description) ?? "";
    }

    getAccessCaption(id): string {
        return this.accessOptions.allOptions.find((access) => access.id == id).value;
    }

    onGroup1FilterChange(searchTerm: string): void {
        let options = this.group1Options.allOptions.filter((it) => this.model.filter((m) => m.GroupFilteringId1?.toString() == it.id).length > 0);
        if (searchTerm && searchTerm.length > 0) {
            options = options.filter((item) => item.value[this.currentLanguage]?.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        this.group1Options.options = options;
    }
    onGroup2FilterChange(searchTerm: string): void {
        let options = this.group2Options.allOptions.filter((it) => this.model.filter((m) => m.GroupFilteringId2?.toString() == it.id).length > 0);
        if (searchTerm && searchTerm.length > 0) {
            options = options.filter((item) => item.value[this.currentLanguage]?.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        this.group2Options.options = options;
    }
    onRolesFilterChange(searchTerm: string): void {
        let options = this.rolesOptions.allOptions.filter((it) => this.model.filter((m) => m?.Roles?.includes(it.id)));
        if (searchTerm && searchTerm.length > 0) {
            options = options.filter((item) =>
                this.wkTranslatePipe.transform(item.value.Description)?.toLowerCase().includes(searchTerm.toLowerCase()),
            );
        }
        this.rolesOptions.options = options;
    }
    onAccessFilterChange(searchTerm: string): void {
        let options = this.accessOptions.allOptions;
        if (searchTerm && searchTerm.length > 0) {
            options = options.filter((item) => item.value.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        this.accessOptions.options = options;
    }
    onFilterValueChange(item, selected) {
        if (selected) {
            this.group1Options.selectedValues.push(item.id);
        } else {
            const index = this.group1Options.selectedValues.indexOf(item.id);
            if (index > -1) {
                this.group1Options.selectedValues.splice(index, 1);
            }
        }
        this.filterModel();
        this.resetToFirstPageIfNecessary();
    }

    resetToFirstPageIfNecessary() {
        if (this.page != 1) {
            this.page = 1;
        }
    }

    onFilterValueChange_Group2(item, selected) {
        if (selected) {
            this.group2Options.selectedValues.push(item.id);
        } else {
            const index = this.group2Options.selectedValues.indexOf(item.id);
            if (index > -1) {
                this.group2Options.selectedValues.splice(index, 1);
            }
        }
        this.filterModel();
        this.resetToFirstPageIfNecessary();
    }

    onAccessFilterValueChange(item, selected) {
        if (selected) {
            this.accessOptions.selectedValues.push(item.id);
        } else {
            const index = this.accessOptions.selectedValues.indexOf(item.id);
            if (index > -1) {
                this.accessOptions.selectedValues.splice(index, 1);
            }
        }
        this.filterModel();
        this.resetToFirstPageIfNecessary();
    }

    onRolesFilterValueChange(item, selected) {
        if (selected) {
            this.rolesOptions.selectedValues.push(item.id);
        } else {
            const index = this.rolesOptions.selectedValues.indexOf(item.id);
            if (index > -1) {
                this.rolesOptions.selectedValues.splice(index, 1);
            }
        }
        this.filterModel();
        this.resetToFirstPageIfNecessary();
    }

    isGroupItemSelected(value, groupObj: OptionModelFilter) {
        return groupObj.selectedValues.includes(value);
    }

    isRoleItemSelected(value) {
        return this.rolesOptions.selectedValues.includes(value);
    }

    isAccessItemSelected(value) {
        return this.accessOptions.selectedValues.includes(value);
    }

    normalizeString(s: string) {
        return s
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replaceAll(/[\u0300-\u036f]/g, "");
    }

    filterModel() {
        this.model = this.allModel;
        if (this.inputTerm !== "") {
            this.model = this.model.filter(
                (it) =>
                    this.normalizeString(it.FirstName).includes(this.normalizeString(this.inputTerm)) ||
                    this.normalizeString(it.LastName).includes(this.normalizeString(this.inputTerm)),
            );
        }
        if (this.group1Options.selectedValues.length > 0) {
            this.model = this.model.filter((it) => this.group1Options.selectedValues.includes(it.GroupFilteringId1.toString()));
        }
        if (this.group2Options.selectedValues.length > 0) {
            this.model = this.model.filter((it) => this.group2Options.selectedValues.includes(it.GroupFilteringId2.toString()));
        }
        if (this.rolesOptions.selectedValues.length > 0) {
            const hasNoneSelected = this.rolesOptions.selectedValues.includes("none");
            const selectedRoleIds = this.rolesOptions.selectedValues.filter((id) => id !== "none");

            this.model = this.model.filter((it) => {
                const hasNoRoles = it.Roles.length === 0;
                const hasSelectedRole =
                    this.currentDashboard.TaxYear >= 2023
                        ? it.Roles.some((r) => selectedRoleIds.includes(r.RoleId))
                        : it.Roles.some((r) => selectedRoleIds.includes(r.Id));

                return (hasNoneSelected && hasNoRoles) || (selectedRoleIds.length > 0 && hasSelectedRole);
            });
        }
        if (this.accessOptions.selectedValues.length > 0) {
            this.model = this.model.filter((it) => this.accessOptions.selectedValues.includes(it.HasAccess.toString()));
        }
        this.model.sort((a, b) => {
            let res = 0;
            if (this.sortMember == "LastName") {
                res = a.LastName.localeCompare(b.LastName, undefined, { sensitivity: "base" });
                if (res === 0) {
                    res = a.FirstName.localeCompare(b.FirstName, undefined, { sensitivity: "base" });
                }
            } else {
                res = a.FirstName.localeCompare(b.FirstName, undefined, { sensitivity: "base" });
                if (res === 0) {
                    res = a.LastName.localeCompare(b.LastName, undefined, { sensitivity: "base" });
                }
            }
            if (this.sortDirAsc === false) {
                res = -res;
            }
            return res;
        });
        this.updateSelectedCount();
    }

    public onSelectAll(): void {
        const start = (this.page - 1) * this.pageSize();
        const pageModel = this.model.slice(start, start + this.pageSize());
        if (pageModel.filter((it) => !it.Selected).length == 0) {
            pageModel.forEach((it) => {
                it.Selected = false;
            });
        } else {
            pageModel.forEach((it) => {
                it.Selected = true;
            });
        }
        this.updateSelectedCount();
    }

    public onUnselectAll(): void {
        this.allModel.forEach((it) => {
            it.Selected = false;
        });
        this.updateSelectedCount();
    }

    changeAccess(access) {
        this.isFormChanged = true;
        this.allModel
            .filter((it) => it.Selected)
            .forEach((it) => {
                it.HasAccess = access;
                it.Selected = false;
            });
        this.updateSelectedCount();
    }

    onChangeAccess() {
        this.bsModalRef = this.modalService.show(DashboardAccessComponent, {
            initialState: {
                title: "Settings.ChangeAccessTitle",
                callback: (access) => this.changeAccess(access),
            },
            class: "change-access-dialog",
        });
    }

    updateSelectedCount() {
        this.selectedCount = this.allModel.filter((it) => it.Selected).length;
        const start = (this.page - 1) * this.pageSize();
        const pageModel = this.model.slice(start, start + this.pageSize());
        this.selectAll = pageModel.filter((it) => !it.Selected).length == 0;
    }

    public showTooltip(e: MouseEvent): void {
        const element = e.target as HTMLElement;
        if (element.nodeName === "TD" && element.offsetWidth < element.scrollWidth) {
            this.tooltipDir().toggle(element);
        } else {
            this.tooltipDir().hide();
        }
    }

    public onSortChange(member) {
        if (member) {
            if (member == this.sortMember) {
                if (this.sortDirAsc === false) {
                    this.sortDirAsc = true;
                } else {
                    this.sortDirAsc = false;
                }
            } else {
                this.sortDirAsc = true;
                this.sortMember = member;
            }
        }
        this.filterModel();
        this.onGroup1FilterChange("");
        this.onGroup2FilterChange("");
        this.onRolesFilterChange("");
    }

    setSettingPagedItems() {
        const startIndex = (this.settingPageNumber - 1) * this.settingPageSize;
        const searchValue = UtilsService.foldDiacritics(this.searchText, this.currentLanguage);
        this.settingPagedItems = this.group1Options.options.filter((item) => {
            const itemValue = UtilsService.foldDiacritics(item.value[this.currentLanguage], this.currentLanguage);
            return itemValue.includes(searchValue);
        });
        this.totalItemsCount = this.settingPagedItems.length;
        this.maxSettingPageSize = Math.ceil(this.settingPagedItems.length / this.settingPageSize);
        this.settingPagedItems = this.settingPagedItems.slice(startIndex, this.settingPageNumber * this.settingPageSize);
    }

    setSettingPagedItems_Group2() {
        const startIndex = (this.settingPageNumber_Group2 - 1) * this.settingPageSize_Group2;
        const searchValue = UtilsService.foldDiacritics(this.searchText_Group2, this.currentLanguage);
        this.settingPagedItems_Group2 = this.group2Options.options.filter((item) => {
            const itemValue = UtilsService.foldDiacritics(item.value[this.currentLanguage], this.currentLanguage);
            return itemValue.includes(searchValue);
        });
        this.totalItemsCount_Group2 = this.settingPagedItems_Group2.length;
        this.maxSettingPageSize_Group2 = Math.ceil(this.settingPagedItems_Group2.length / this.settingPageSize_Group2);
        this.settingPagedItems_Group2 = this.settingPagedItems_Group2.slice(startIndex, this.settingPageNumber_Group2 * this.settingPageSize_Group2);
    }

    setSettingPagedItems_Roles() {
        const startIndex = (this.settingPageNumber_Roles - 1) * this.settingPageSize_Roles;
        const searchValue = UtilsService.foldDiacritics(this.searchText_Roles, this.currentLanguage);
        this.settingPagedItems_Roles = this.rolesOptions.options.filter((item) => {
            const itemValue = UtilsService.foldDiacritics(this.wkTranslatePipe.transform(item.value.Description) ?? "", this.currentLanguage);
            return itemValue.includes(searchValue);
        });
        this.totalItemsCount_Roles = this.settingPagedItems_Roles.length;
        this.maxSettingPageSize_Roles = Math.ceil(this.settingPagedItems_Roles.length / this.settingPageSize_Roles);
        this.settingPagedItems_Roles = this.settingPagedItems_Roles.slice(startIndex, this.settingPageNumber_Roles * this.settingPageSize_Roles);
    }
    setSettingPagedItems_Access() {
        const startIndex = (this.settingPageNumber_Access - 1) * this.settingPageSize_Access;
        const searchValue = UtilsService.foldDiacritics(this.searchText__Access, this.currentLanguage);
        this.settingPagedItems_Access = this.accessOptions.options.filter((item) => {
            const itemValue = UtilsService.foldDiacritics(item.value[this.currentLanguage], this.currentLanguage);
            return itemValue.includes(searchValue);
        });
        this.totalItemsCount_Access = this.settingPagedItems_Access.length;
        this.maxSettingPageSize_Access = Math.ceil(this.settingPagedItems_Access.length / this.settingPageSize_Access);
        this.settingPagedItems_Access = this.settingPagedItems_Access.slice(startIndex, this.settingPageNumber_Access * this.settingPageSize_Access);
    }
    onSearchChange(e: any, group: "group1" | "group2") {
        if (group === "group1") {
            this.searchText = e;
            this.settingPageNumber = 1;
            this.setSettingPagedItems();
        } else if (group === "group2") {
            this.searchText_Group2 = e;
            this.settingPageNumber_Group2 = 1;
            this.setSettingPagedItems_Group2();
        }
    }
    onSearchChange_Roles(e: any) {
        this.searchText_Roles = e;
        this.settingPageNumber_Roles = 1;
        this.setSettingPagedItems_Roles();
    }

    onSearchChange_Access(e: any) {
        this.searchText__Access = e;
        this.settingPageNumber_Access = 1;
        this.setSettingPagedItems_Access();
    }

    public onShow(): void {
        this.changeDetectorRef.detectChanges();
        this.searchText = "";
        this.setSettingPagedItems();
    }
    public onShow_Group2(): void {
        this.changeDetectorRef.detectChanges();
        this.searchText_Group2 = "";
        this.setSettingPagedItems_Group2();
    }
    public onShow_Roles(): void {
        this.changeDetectorRef.detectChanges();
        this.searchText_Roles = "";
        this.setSettingPagedItems_Roles();
    }
    public onShow_Access(): void {
        this.changeDetectorRef.detectChanges();
        this.searchText__Access = "";
        this.setSettingPagedItems_Access();
    }

    onSettingPageChange(e: any, group: "group1" | "group2") {
        if (group === "group1") {
            this.settingPageNumber = e;
            this.setSettingPagedItems();
        } else if (group === "group2") {
            this.settingPageNumber_Group2 = e;
            this.setSettingPagedItems_Group2();
        }
    }

    onSettingPageChange_Roles(e: any) {
        this.settingPageNumber_Roles = e;
        this.setSettingPagedItems_Roles();
    }
    onSettingPageChange_Access(e: any) {
        this.settingPageNumber_Access = e;
        this.setSettingPagedItems_Access();
    }
    public onSelectAllGroups(group: "group1" | "group2"): void {
        if (group === "group1") {
            this.group1Options.selectedValues = this.settingPagedItems.map((option) => option.id);
        } else if (group === "group2") {
            this.group2Options.selectedValues = this.settingPagedItems_Group2.map((option) => option.id);
        }
        this.filterModel();
    }
    public onClearAllGroups(group: "group1" | "group2"): void {
        if (group === "group1") {
            this.group1Options.selectedValues = [];
        } else if (group === "group2") {
            this.group2Options.selectedValues = [];
        }
        this.filterModel();
    }

    public onSelectAllRoles(): void {
        this.rolesOptions.selectedValues = this.settingPagedItems_Roles.map((option) => option.id);
        this.filterModel();
    }

    public onClearAllRoles(): void {
        this.rolesOptions.selectedValues = [];
        this.filterModel();
    }

    public onSelectAllAccess(): void {
        this.accessOptions.selectedValues = this.settingPagedItems_Access.map((option) => option.id);
        this.filterModel();
    }

    public onClearAllAccess(): void {
        this.accessOptions.selectedValues = [];
        this.filterModel();
    }
}
